/**
 * 58mm Thermal Receipt Generator
 * Generates HTML receipt optimized for 58mm thermal printers (~32 chars wide)
 * NO LOGO - text only for thermal printing
 */

// Helper to format currency
const formatCurrency = (amount) => {
  return `$${parseFloat(amount || 0).toFixed(2)}`;
};

// Helper to format date
const formatDate = (date) => {
  if (!date) return new Date().toLocaleDateString();
  return new Date(date).toLocaleDateString();
};

// Helper to truncate text to fit receipt width
const truncate = (text, maxLength) => {
  if (!text) return '';
  return text.length > maxLength ? text.substring(0, maxLength - 1) + '…' : text;
};

// Helper to pad/align text for receipt formatting
const padLine = (left, right, width = 32) => {
  const leftStr = String(left || '');
  const rightStr = String(right || '');
  const spaces = width - leftStr.length - rightStr.length;
  return leftStr + ' '.repeat(Math.max(1, spaces)) + rightStr;
};

// Helper to center text
const centerText = (text, width = 32) => {
  const str = String(text || '');
  const padding = Math.max(0, Math.floor((width - str.length) / 2));
  return ' '.repeat(padding) + str;
};

/**
 * Generate receipt HTML from invoice data
 * @param {Object} invoice - Invoice data object
 * @param {Object} accountSettings - Account settings with company info
 * @returns {string} HTML string for receipt
 */
export const generateReceiptHTML = (invoice, accountSettings) => {
  const job = invoice.job || invoice;

  // Calculate totals if not already provided
  let products = invoice.products || [];
  let subtotal = invoice.productCost || 0;
  let totalQuantity = invoice.totalQuantity || 0;

  // If products not provided, calculate from job.sales
  if (products.length === 0 && job.sales && Array.isArray(job.sales)) {
    job.sales.forEach((sale) => {
      if (sale.saleItems && Array.isArray(sale.saleItems)) {
        sale.saleItems.forEach((saleItem) => {
          const unitPrice = parseFloat(saleItem.unitPrice || saleItem.product?.wholesalePrice || saleItem.product?.unitPrice || 0);
          const quantity = parseInt(saleItem.quantity) || 1;
          const lineTotal = unitPrice * quantity;
          subtotal += lineTotal;
          totalQuantity += quantity;
          products.push({
            name: saleItem.product?.name || 'Unknown Product',
            quantity,
            unitPrice,
            lineTotal
          });
        });
      }
    });
  }

  const discountAmount = invoice.discountAmount || 0;
  const discountLabel = invoice.discountLabel || '';
  const taxAmount = invoice.taxAmount || 0;
  const totalCost = invoice.totalCost || (subtotal - discountAmount + taxAmount);
  const totalPaid = invoice.totalPaid || 0;
  const balanceDue = invoice.balanceDue || (totalCost - totalPaid);
  const taxRate = accountSettings?.taxRate || 6;

  // Build receipt lines
  const lines = [];
  const divider = '================================';
  const thinDivider = '--------------------------------';

  // Header - Company Info (NO LOGO)
  lines.push(divider);
  lines.push(centerText(accountSettings?.companyName || 'COMPANY NAME'));
  if (accountSettings?.address) {
    const address = accountSettings.address;
    if (address.length > 32) {
      const parts = address.split(',').map(p => p.trim());
      parts.forEach(part => {
        if (part) lines.push(centerText(truncate(part, 32)));
      });
    } else {
      lines.push(centerText(address));
    }
  }
  if (accountSettings?.phone) {
    lines.push(centerText(accountSettings.phone));
  }
  if (accountSettings?.email) {
    lines.push(centerText(truncate(accountSettings.email, 32)));
  }
  lines.push(divider);

  // Invoice/Receipt info
  lines.push('');
  lines.push(padLine('RECEIPT', `#${invoice.id || job.id || '000'}`));
  lines.push(padLine('Date:', formatDate(invoice.date || job.orderDate || job.createdAt)));
  lines.push(padLine('Job #:', `JOB-${job.id || '000'}`));
  lines.push('');

  // Customer info
  lines.push(thinDivider);
  lines.push('CUSTOMER:');
  lines.push(truncate(invoice.customerName || job.customer?.name || 'Customer', 32));
  if (job.customer?.phone) {
    lines.push(job.customer.phone);
  }
  if (job.customer?.addressLine1) {
    lines.push(truncate(job.customer.addressLine1, 32));
  }

  // Products header
  lines.push(thinDivider);
  lines.push('Qty  Item              Price');
  lines.push(thinDivider);

  // Product lines
  if (products.length > 0) {
    products.forEach((product) => {
      const qty = String(product.quantity).padStart(3, ' ');
      const name = truncate(product.name, 16).padEnd(16, ' ');
      const price = formatCurrency(product.lineTotal).padStart(8, ' ');
      lines.push(`${qty}  ${name} ${price}`);
    });
  } else {
    lines.push(centerText('No items'));
  }

  // Totals section
  lines.push(thinDivider);
  lines.push(padLine('Subtotal:', formatCurrency(subtotal)));

  if (discountAmount > 0) {
    lines.push(padLine('Discount:', `-${formatCurrency(discountAmount)}`));
  }

  if (taxAmount > 0) {
    lines.push(padLine(`Tax (${taxRate}%):`, formatCurrency(taxAmount)));
  }

  lines.push(thinDivider);
  lines.push(padLine('TOTAL:', formatCurrency(totalCost)));

  if (totalPaid > 0) {
    lines.push(padLine('Paid:', `-${formatCurrency(totalPaid)}`));
  }

  // Balance Due - highlight if non-zero
  if (balanceDue !== 0) {
    lines.push(divider);
    if (balanceDue > 0) {
      lines.push(padLine('BALANCE DUE:', formatCurrency(balanceDue)));
    } else {
      lines.push(padLine('CREDIT:', formatCurrency(Math.abs(balanceDue))));
    }
    lines.push(divider);
  } else {
    lines.push(divider);
    lines.push(centerText('*** PAID IN FULL ***'));
    lines.push(divider);
  }

  // Payment history (if any)
  if (job.payments && job.payments.length > 0) {
    lines.push('');
    lines.push('PAYMENT HISTORY:');
    job.payments.forEach((payment) => {
      const paymentDate = payment.paymentDate ? new Date(payment.paymentDate).toLocaleDateString() : '';
      const type = payment.paymentType || 'PAYMENT';
      const amt = formatCurrency(payment.amount);
      lines.push(`${paymentDate} ${type} ${amt}`);
    });
  }

  // Fulfillment info (if set)
  if (job.fulfillmentStatus && job.fulfillmentStatus !== 'PENDING') {
    lines.push('');
    lines.push(thinDivider);
    lines.push(padLine('Status:', job.fulfillmentStatus));
    if (job.shippingCarrier) {
      lines.push(padLine('Carrier:', job.shippingCarrier));
    }
    if (job.trackingNumber) {
      lines.push(`Track: ${truncate(job.trackingNumber, 26)}`);
    }
  }

  // Footer
  lines.push('');
  lines.push(thinDivider);
  lines.push(centerText('Thank you for your'));
  lines.push(centerText('business!'));
  lines.push(thinDivider);
  lines.push('');

  // Build HTML - NO LOGO for thermal receipt
  const receiptContent = lines.join('\n');

  return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Receipt #${invoice.id || job.id || '000'}</title>
  <style>
    @media print {
      @page {
        size: 58mm auto;
        margin: 0;
      }
      body {
        margin: 0;
        padding: 2mm;
      }
    }

    body {
      font-family: 'Courier New', Courier, monospace;
      font-size: 12px;
      line-height: 1.2;
      width: 58mm;
      margin: 0 auto;
      padding: 2mm;
      background: white;
      color: black;
    }

    .receipt {
      white-space: pre;
      font-size: 11px;
      line-height: 1.3;
    }

    @media screen {
      body {
        background: #f0f0f0;
        padding: 20px;
      }
      .receipt-wrapper {
        background: white;
        padding: 10px;
        box-shadow: 0 2px 10px rgba(0,0,0,0.1);
        width: 58mm;
        margin: 0 auto;
      }
    }
  </style>
</head>
<body>
  <div class="receipt-wrapper">
    <pre class="receipt">${receiptContent}</pre>
  </div>
</body>
</html>`;
};

/**
 * Print receipt using browser's print dialog
 * @param {Object} invoice - Invoice data object
 * @param {Object} accountSettings - Account settings with company info
 */
export const printReceipt = (invoice, accountSettings) => {
  const html = generateReceiptHTML(invoice, accountSettings);

  // Create a new window for printing
  const printWindow = window.open('', '_blank', 'width=300,height=600');

  if (!printWindow) {
    throw new Error('Popup blocked. Please allow popups to print receipts.');
  }

  printWindow.document.write(html);
  printWindow.document.close();

  // Wait for content to load then print
  printWindow.onload = () => {
    printWindow.focus();
    printWindow.print();
  };

  return true;
};

export default {
  generateReceiptHTML,
  printReceipt
};
